import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { Helmet } from '@/pages/home/components/SEOHead';
import { AboutSchema } from '@/components/feature/SchemaOrg';
import { brandIntro, reputationItems, milestones } from '@/mocks/about';
import { faqData as homeFaq } from '@/mocks/home';
import { useState } from 'react';

export default function About() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const allFaq = [...homeFaq.slice(0, 5)];

  return (
    <>
      <Helmet
        title="关于我们 - 52662鼎盛平台 | 品牌介绍与联系方式"
        description="了解52662鼎盛平台的品牌故事、发展历程、核心优势与联系方式。官方认证正规平台，累计服务500万+用户，值得您的信赖。"
        canonicalUrl="/about"
      />
      <AboutSchema />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb />

        {/* Brand Intro */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <span className="inline-block px-3 py-1 rounded-full bg-primary-50 text-primary-700 text-xs font-medium mb-4">
                {brandIntro.subtitle}
              </span>
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
                {brandIntro.title}
              </h1>
            </div>

            <div className="max-w-4xl mx-auto">
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 md:p-10 mb-10">
                {brandIntro.description.map((para, i) => (
                  <p key={i} className="text-foreground-700 text-sm md:text-base leading-relaxed mb-4 last:mb-0">
                    {para}
                  </p>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="bg-background-100/60 rounded-lg p-6 text-center border border-background-200/50">
                  <p className="text-xs text-foreground-500 mb-2 uppercase tracking-wider">我们的愿景</p>
                  <p className="font-heading text-lg font-bold text-foreground-900">
                    {brandIntro.vision}
                  </p>
                </div>
                <div className="bg-background-100/60 rounded-lg p-6 text-center border border-background-200/50">
                  <p className="text-xs text-foreground-500 mb-2 uppercase tracking-wider">我们的使命</p>
                  <p className="font-heading text-lg font-bold text-foreground-900">
                    {brandIntro.mission}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Reputation */}
        <section className="py-16 md:py-20 bg-background-100/60">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                信誉与资质
              </h2>
              <p className="text-foreground-600 text-sm max-w-2xl mx-auto">
                多项权威认证与行业荣誉，见证鼎盛平台的品牌实力
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {reputationItems.map((item, i) => (
                <div key={i} className="bg-background-50 rounded-lg p-6 border border-background-200/50 flex gap-4">
                  <div className="w-10 h-10 rounded-lg bg-accent-50 flex items-center justify-center flex-shrink-0">
                    <i className={`${item.icon} text-accent-600 text-lg`} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground-900 text-sm mb-1.5">{item.title}</h3>
                    <p className="text-foreground-600 text-xs leading-relaxed">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Milestones */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                发展历程
              </h2>
              <p className="text-foreground-600 text-sm max-w-2xl mx-auto">
                从创立至今，每一步都坚实有力
              </p>
            </div>

            <div className="max-w-3xl mx-auto relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 md:-translate-x-px" />

              {milestones.map((item, i) => (
                <div
                  key={i}
                  className={`relative flex items-start gap-6 mb-8 last:mb-0 ${
                    i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  <div className="md:w-1/2 flex items-center gap-6">
                    <div className={`flex-1 text-right ${i % 2 === 0 ? 'md:text-right' : 'md:text-left'} pl-12 md:pl-0`}>
                      <div className={`bg-background-100/60 rounded-lg p-4 border border-background-200/50 ${i % 2 === 0 ? 'md:mr-6' : 'md:ml-6'}`}>
                        <span className="inline-block px-2 py-0.5 rounded bg-primary-50 text-primary-700 text-xs font-semibold mb-2">
                          {item.year}
                        </span>
                        <p className="text-foreground-700 text-sm">{item.event}</p>
                      </div>
                    </div>
                  </div>
                  <div className="hidden md:block md:w-1/2" />
                  <div className="absolute left-4 md:left-1/2 top-3 w-2.5 h-2.5 rounded-full bg-primary-500 border-2 border-background-50 -translate-x-1/2" />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="py-16 md:py-20 bg-background-100/60">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                联系我们
              </h2>
              <p className="text-foreground-600 text-sm max-w-2xl mx-auto">
                如有任何问题，欢迎通过以下方式联系我们
              </p>
            </div>

            <div className="max-w-4xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-5">
              <div className="bg-background-50 rounded-lg p-6 border border-background-200/50 text-center">
                <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-primary-50 flex items-center justify-center">
                  <i className="ri-phone-line text-primary-600 text-xl" />
                </div>
                <h3 className="font-semibold text-foreground-900 text-sm mb-2">客服热线</h3>
                <p className="text-foreground-600 text-sm">400-XXX-XXXX</p>
              </div>
              <div className="bg-background-50 rounded-lg p-6 border border-background-200/50 text-center">
                <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-primary-50 flex items-center justify-center">
                  <i className="ri-mail-line text-primary-600 text-xl" />
                </div>
                <h3 className="font-semibold text-foreground-900 text-sm mb-2">电子邮箱</h3>
                <p className="text-foreground-600 text-sm">support@52662dingsheng.com</p>
              </div>
              <div className="bg-background-50 rounded-lg p-6 border border-background-200/50 text-center">
                <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-primary-50 flex items-center justify-center">
                  <i className="ri-time-line text-primary-600 text-xl" />
                </div>
                <h3 className="font-semibold text-foreground-900 text-sm mb-2">服务时间</h3>
                <p className="text-foreground-600 text-sm">7×24小时全天候</p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                常见问题
              </h2>
              <p className="text-foreground-600 text-sm">快速解答您的疑问</p>
            </div>

            <div className="flex flex-col gap-3">
              {allFaq.map((faq, i) => (
                <div
                  key={i}
                  className="bg-background-50 border border-background-200/50 rounded-lg overflow-hidden"
                >
                  <button
                    className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer hover:bg-background-100/50 transition-colors duration-200"
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  >
                    <span className="text-sm font-semibold text-foreground-900 pr-4">{faq.question}</span>
                    <i className={`ri-${openFaq === i ? 'subtract' : 'add'}-line text-foreground-500 text-lg flex-shrink-0 transition-transform duration-200`} />
                  </button>
                  {openFaq === i && (
                    <div className="px-5 pb-4">
                      <p className="text-foreground-600 text-sm leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}